import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Loader2, 
  Users,
  CheckCircle2,
  Clock,
  AlertCircle,
  Eye,
  Send,
  ClipboardCheck,
  LogOut,
  ChevronRight
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ManagerDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [directReports, setDirectReports] = useState([]);
  const [pendingValidations, setPendingValidations] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [userInfo, setUserInfo] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    const storedUser = localStorage.getItem("userInfo");
    
    if (!token) {
      navigate("/login");
      return;
    }
    
    if (storedUser) {
      const user = JSON.parse(storedUser);
      setUserInfo(user);
    }
    
    fetchData();
  }, [navigate]);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` }
  });

  const fetchData = async () => {
    try {
      const [statsRes, reportsRes, validationsRes] = await Promise.all([
        axios.get(`${API}/manager/stats`, getAuthHeaders()),
        axios.get(`${API}/manager/direct-reports`, getAuthHeaders()),
        axios.get(`${API}/manager/pending-validations`, getAuthHeaders())
      ]);
      
      setStats(statsRes.data);
      setDirectReports(reportsRes.data.directReports);
      setPendingValidations(validationsRes.data.pendingValidations);
    } catch (err) {
      console.error("Error fetching data:", err);
      if (err.response?.status === 401) {
        localStorage.clear();
        navigate("/login");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchDirectReports = async () => {
    try {
      const params = statusFilter !== "all" ? `?status=${statusFilter}` : "";
      const response = await axios.get(`${API}/manager/direct-reports${params}`, getAuthHeaders());
      setDirectReports(response.data.directReports);
    } catch (err) {
      console.error("Error fetching direct reports:", err);
    }
  };

  useEffect(() => {
    if (!loading) {
      fetchDirectReports();
    }
  }, [statusFilter]);

  const handleLogout = () => {
    localStorage.clear();
    navigate("/login");
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-emerald-100 text-emerald-700">Completed</Badge>;
      case 'started':
        return <Badge className="bg-amber-100 text-amber-700">In Progress</Badge>;
      default:
        return <Badge className="bg-slate-100 text-slate-600">Not Started</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8fafc]">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-[#1a365d] mx-auto mb-4" />
          <p className="text-muted-foreground">Loading team dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#1a365d] text-white shadow-lg">
        <div className="max-w-[1200px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xl">S</span>
              </div>
              <div>
                <h1 className="text-lg font-semibold font-['Manrope']">
                  Team Dashboard
                </h1>
                <p className="text-sm text-white/70">
                  My Direct Reports
                </p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              {userInfo && (
                <div className="text-right hidden sm:block">
                  <p className="text-sm font-medium">{userInfo.fullName}</p>
                  <p className="text-xs text-white/70">{userInfo.jobTitle}</p>
                </div>
              )}
              <div className="w-10 h-10 bg-[#B8860B] rounded-full flex items-center justify-center text-sm font-semibold">
                {userInfo?.fullName?.split(' ').map(n => n[0]).join('').slice(0, 2) || 'MG'}
              </div>
              <Button 
                variant="ghost" 
                size="icon" 
                className="text-white/70 hover:text-white hover:bg-white/10"
                onClick={handleLogout}
              >
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-6 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card className="bg-white shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#1a365d]">{stats?.total || 0}</p>
                  <p className="text-xs text-muted-foreground uppercase">Total</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-emerald-600">{stats?.completed || 0}</p>
                  <p className="text-xs text-muted-foreground uppercase">Completed</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-600">{(stats?.started || 0) + (stats?.pending || 0)}</p>
                  <p className="text-xs text-muted-foreground uppercase">Pending</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Pending Validations */}
        {pendingValidations.length > 0 && (
          <Card className="bg-amber-50 border-amber-200 shadow-sm mb-6">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <ClipboardCheck className="w-5 h-5 text-amber-600" />
                <CardTitle className="text-base font-semibold text-amber-800">
                  Pending Validations ({pendingValidations.length})
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {pendingValidations.map((person) => (
                  <div 
                    key={person.id}
                    className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-200"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-amber-100 rounded-full flex items-center justify-center text-sm font-medium text-amber-700">
                        {person.fullName?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                      </div>
                      <div>
                        <p className="font-medium text-sm">{person.fullName}</p>
                        <p className="text-xs text-muted-foreground">
                          Completed {person.completedAt ? new Date(person.completedAt).toLocaleDateString() : 'recently'}
                        </p>
                      </div>
                    </div>
                    <Button size="sm" className="gap-2 bg-amber-600 hover:bg-amber-700">
                      Review & Validate
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Direct Reports */}
        <Card className="bg-white shadow-sm">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold text-[#1a365d]">
                My Direct Reports
              </CardTitle>
            </div>
            
            <div className="flex gap-2 mt-4">
              {['all', 'completed', 'started', 'pending'].map((status) => (
                <Button
                  key={status}
                  variant={statusFilter === status ? "default" : "outline"}
                  size="sm"
                  onClick={() => setStatusFilter(status)}
                  className={statusFilter === status ? 'bg-[#1a365d]' : ''}
                >
                  {status === 'all' ? 'All' : 
                   status === 'completed' ? 'Completed' :
                   status === 'started' ? 'In Progress' : 'Not Started'}
                </Button>
              ))}
            </div>
          </CardHeader>
          <CardContent>
            {directReports.length === 0 ? (
              <div className="text-center py-10 text-muted-foreground">
                <Users className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p>No direct reports assigned</p>
              </div>
            ) : (
              <ScrollArea className="h-[400px]">
                <div className="space-y-2">
                  {directReports.map((person) => (
                    <div 
                      key={person.id}
                      className="flex items-center justify-between p-4 rounded-lg border hover:bg-slate-50 transition-colors"
                    >
                      <div className="flex items-center gap-4 flex-1">
                        <div className="w-10 h-10 bg-[#1a365d]/10 rounded-full flex items-center justify-center text-sm font-medium text-[#1a365d]">
                          {person.fullName?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium">{person.fullName}</p>
                          <p className="text-sm text-muted-foreground">
                            {person.jobTitle} • {person.regionFlag} {person.region}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        {getStatusBadge(person.status)}
                        <div className="w-28">
                          <div className="flex items-center gap-2">
                            <Progress value={person.progressPercentage} className="h-2 flex-1" />
                            <span className="text-xs font-medium w-10 text-right">
                              {person.progressPercentage}%
                            </span>
                          </div>
                        </div>
                        <div className="flex gap-1">
                          {person.status === 'completed' ? (
                            <Button size="sm" variant="outline" className="gap-1">
                              <Eye className="w-3 h-3" />
                              View
                            </Button>
                          ) : (
                            <Button size="sm" variant="outline" className="gap-1">
                              <Send className="w-3 h-3" />
                              Remind
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
